import React, {
    useEffect,
    useRef,
    useState,
    ForwardedRef,
    forwardRef,
    useImperativeHandle
} from 'react'
import { throttle } from 'lodash'

import { ListMethods } from '../../utils/types'

interface LazyLoadedListProps  {
    items: any[],
    searchKey?: string,
    batch?: number,
    itemHeight?: number
}

const LazyList = forwardRef<ListMethods, LazyLoadedListProps>(
    (
        { items, searchKey = '', batch, itemHeight = 32 }: LazyLoadedListProps,
        ref: ForwardedRef<ListMethods>
    ) => {
    const listEndRef = useRef<HTMLDivElement | null>(null)
    const itemRefs = useRef<{[key: string]: any}>({})
    const fastScrollModeRef = useRef<boolean>(false)
    const lastScrollPosRef = useRef<number>(window.scrollY)
    const scrollTimeoutRef = useRef<any>(null)
    const [visibleItems, setVisibleItems] = useState<any[]>([])
    const batchSize = useRef<number>(batch || 20)
    const rootMargin = '200px'
  
    useEffect(() => {
        batchSize.current = batch ? batchSize.current : Math.round((window.innerHeight - 90) / itemHeight) || batchSize.current
        if (searchKey.length) setVisibleItems(items.filter(i => (Object.keys(i)[0].toLowerCase().startsWith(searchKey.toLowerCase()))).slice(0, batchSize.current))
        else setVisibleItems(items.slice(0, batchSize.current))
    }, [items, searchKey])  // eslint-disable-line

    // Expose scrollToItem to parent component
    useImperativeHandle(ref, () => ({
        scrollToItem: async (key: string) => {
          // If item is already rendered, scroll to it
          if (itemRefs.current[key]) {
            itemRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            return
          }
          // If item exists in full list but isn't rendered
          const itemIndex = items.findIndex(i => Object.keys(i)[0].toLowerCase() === key.toLowerCase())
          if (itemIndex >= 0) {
            // Expand visible items to include target item
            const newVisibleItems = items.slice(0, Math.max(itemIndex + 5, visibleItems.length + 10))
            setVisibleItems(newVisibleItems)
            // Wait for DOM update
            await new Promise(resolve => setTimeout(resolve, 50))
            // Scroll to item after it's rendered
            if (itemRefs.current[key]) itemRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }
    }))

    const isElementNearViewport = (el: any) => {
        if (!el) return false
        const rect = el.getBoundingClientRect()
        const buffer = parseInt(rootMargin) || 300
        return (
            rect.top < window.innerHeight + buffer && 
            rect.bottom > -buffer
        )
    }

    const onIntersect = () => {
        if (visibleItems.length < items.length) {
            if (searchKey.length) {
                setVisibleItems(prev => {
                    const temp = items.filter(i => (Object.keys(i)[0].toLowerCase().startsWith(searchKey.toLowerCase()))).slice(prev.length, prev.length + batchSize.current)
                    return [...prev, ...temp]
                })
            } else setVisibleItems(prev => ([...prev, ...items.slice(prev.length, prev.length + batchSize.current)]))
        }
    }
  
    useEffect(() => {
        const listRef = listEndRef.current

        const OBS = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                requestAnimationFrame(() => {
                    if (entry.isIntersecting) onIntersect()
                })
            })
        }, {
            root: null,
            rootMargin: rootMargin,
            threshold: [0, 0.1, 0.3, 0.5, 0.7, 0.9, 1]
        })

        // Backup scroll handler for fast scrolling detection
        const handleScroll = throttle(() => {
            const currentScroll = window.scrollY
            const scrollDiff = Math.abs(currentScroll - lastScrollPosRef.current)
            lastScrollPosRef.current = currentScroll
            // Detect fast scroll (more than 500px difference between checks)
            if (scrollDiff > 500) {
                fastScrollModeRef.current = true
                // Force check all observed elements during fast scroll
                OBS.takeRecords().forEach((entry: any) => {
                    if (isElementNearViewport(entry.target)) onIntersect()
                })
            }
            // Reset fast scroll mode after 300ms of no scrolling
            clearTimeout(scrollTimeoutRef.current)
            scrollTimeoutRef.current = setTimeout(() => fastScrollModeRef.current = false, 300)
        }, 100)

        // Add event listeners
        window.addEventListener('scroll', handleScroll, { passive: true })

        // Initial observer
        if (listRef) OBS.observe(listRef)

        return () => {
            // Cleanup
            window.removeEventListener('scroll', handleScroll)
            if (listRef) OBS.unobserve(listRef)
            OBS.disconnect()
            clearTimeout(scrollTimeoutRef.current)
        }
    }, [])  // eslint-disable-line
  
    return (
        <>
            {visibleItems.map((i, index) => (
                <div key={index} ref={el => itemRefs.current[Object.keys(i)[0]] = el}>
                    {Object.values(i)[0]}
                </div>
            ))}
            <div ref={listEndRef} />
        </>
    )
})

export default LazyList
